import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  ThemeProvider,
  CssBaseline,
  alpha,
  Dialog,
  Typography,
} from '@mui/material';
import { createAppTheme, ThemeStyles } from './theme';
import './fonts.css';
import Fade from '@mui/material/Fade';
import { debounce } from 'lodash';
import Cookies from 'js-cookie';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import CategoryManager from './components/CategoryManager';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useApi } from './hooks/useApi';
import { useAuth } from './contexts/AuthContext';
import { onSnapshot, collection, doc, updateDoc, query, where, getDocs, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import UserManagement from './components/UserManagement';
import Layout from './components/Layout';
import { CategoriesProvider, useCategories } from './contexts/CategoriesContext';
import ChainProvider from './contexts/ChainContext';
import HotelDetails from './components/HotelDetails';
import HotelManagement from './components/HotelManagement';
import { HeroIconNames } from './components/icons';
import ThemeSettings from './components/ThemeSettings';
import ThemePreview from './components/ThemePreview';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useEmojiCursor } from './hooks/useEmojiCursor';
import EnhancedHandbookLibrary from './components/HandbookLibrary/EnhancedHandbookLibrary';
import ChainManager from './components/chain/ChainManager';
import ActivityLogs from './components/Admin/ActivityLogs';
import HotelDashboard from './components/dashboard/HotelDashboard';
import ForceRefresh from './components/Admin/ForceRefresh';
import ScriptsPage from './components/Scripts/ScriptsPage';
import FeatureRequestPage from './components/FeatureRequest/FeatureRequestPage';
import Documentation from './components/Documentation/Documentation';
import confetti from 'canvas-confetti';
import useDocumentTitle from './hooks/useDocumentTitle';
import NoHotelSelected from './components/NoHotelSelectedCampaign';
import NoHotelSelectedSimple from './components/NoHotelSelectedSimple';
import StatisticsDialog from './components/dialogs/StatisticsDialog';
import UpdateNotification from './components/dialogs/UpdateNotification';
import { getRandomMessage } from './utils/messages';
import { initializeTheme } from './utils/theme';
import Header from './components/layout/Header';
import HotelList from './components/hotels/HotelList';
import WeeklyStatistics from './components/Statistics/WeeklyStatistics';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { chainCache } from './contexts/ChainContext';
import { getAuth } from 'firebase/auth';
import GoogleContactsPage from './components/GoogleContacts/GoogleContactsPage';
import useAnalytics from './hooks/useAnalytics';
import NotFoundPage from './components/NotFoundPage';

/** Renders 404 when URL is /app/<unknown>; allows /app and /app/hotel/:id. */
const AppShellOrNotFound = ({ children }) => {
  const { '*': splat } = useParams();
  const s = splat != null ? String(splat).trim() : '';
  if (s === '') return children;
  // Allow /app/hotel/:propertyNumber (visiting a hotel)
  if (s.startsWith('hotel/')) return children;
  return <NotFoundPage />;
};

// Helper function to get display name for a chain from the db value - now also uses the chainCache
// Cache for chain display names to prevent repeated lookups
const chainDisplayNameCache = new Map();

const getChainDisplayName = (dbValue) => {
  if (!dbValue) return '';
  
  // Check local cache first
  if (chainDisplayNameCache.has(dbValue)) {
    return chainDisplayNameCache.get(dbValue);
  }
  
  try {
    // First try the global chainCache which is available before React context
    if (chainCache.loaded) {
      // Try finding the chain in the cache
      const chain = chainCache.chains.find(c => 
        c.name && c.name.toLowerCase() === dbValue.toLowerCase()
      );
      
      if (chain) {
        chainDisplayNameCache.set(dbValue, chain.name);
        return chain.name;
      }
    }
    
    // Fallback to window.chainContext for backward compatibility
    const chainContext = window.chainContext;
    
    if (!chainContext) {
      chainDisplayNameCache.set(dbValue, dbValue); // Cache the result
      return dbValue;
    }
    
    if (!Array.isArray(chainContext.chains)) {
      chainDisplayNameCache.set(dbValue, dbValue); // Cache the result
      return dbValue;
    }
    
    const chain = chainContext.chains.find(chain => 
      chain.name.toLowerCase() === dbValue.toLowerCase()
    );
    
    const result = chain?.name || dbValue;
    chainDisplayNameCache.set(dbValue, result); // Cache the result
    return result;
  } catch (error) {
    console.error('Error in getChainDisplayName:', error);
    chainDisplayNameCache.set(dbValue, dbValue); // Cache the result even in case of error
    return dbValue || '';
  }
};

// Note: Centralized styles moved to theme engine
// Use ThemeStyles from './theme' instead of local commonStyles

// Add helper function to get category value
const getCategoryValue = (hotel, categoryName) => {
  if (!hotel || !categoryName) return null;
  
  // Handle both string and object category names
  const fieldName = typeof categoryName === 'object' && categoryName.fieldName 
    ? categoryName.fieldName.toLowerCase()
    : String(categoryName).toLowerCase();
    
  // First try to get the value directly from the hotel object
  if (hotel[fieldName] !== undefined) {
    return hotel[fieldName];
  }
  
  // Then try to get it from categoryData if it exists
  if (hotel.categoryData) {
  const category = hotel.categoryData[`category_${fieldName}`];
  return category?.value || null;
  }
  
  return null;
};

// Get random icon for empty state
const getRandomIcon = () => {
  const icons = [
    HeroIconNames.HOTEL,
    HeroIconNames.RESTAURANT,
    HeroIconNames.SPA,
    HeroIconNames.GOTHENBURG,
    HeroIconNames.HELSINKI,
    HeroIconNames.MEETING,
    HeroIconNames.STOCKHOLM,
    HeroIconNames.BERGEN,
    HeroIconNames.STRAWBERRY_WEBSITE,
    HeroIconNames.BONUS_NIGHT,
    HeroIconNames.GUARANTEED_ROOM
  ];
  return icons[Math.floor(Math.random() * icons.length)];
};

// Move helper functions outside component to prevent recreation
const normalizeScandinavianCharacters = (str) => {
  if (!str) return '';
  // Ensure str is a string before calling toLowerCase()
  const strValue = String(str);
  return strValue.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[æ]/g, 'ae')
    .replace(/[ø]/g, 'o')
    .replace(/[å]/g, 'a');
};

// Cache for normalized strings
const normalizedCache = new Map();
const getNormalizedString = (str) => {
  if (!str) return '';
  if (normalizedCache.has(str)) {
    return normalizedCache.get(str);
  }
  const normalized = normalizeScandinavianCharacters(str);
  normalizedCache.set(str, normalized);
  return normalized;
};

function AppContent() {
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode !== null ? JSON.parse(savedMode) : true;
  });
  const [pinkMode, setPinkMode] = useState(false);  // Add pink mode state
  const [themeLoaded, setThemeLoaded] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [, setIsAutoSelected] = useState(false); // Value not read, but setter used for tracking
  const [isEditing, setIsEditing] = useState(false);
  const [editedHotel, setEditedHotel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hotelSaveStatus, setHotelSaveStatus] = useState(null); // 'success' | 'error' | null
  const [hotelSaveStatusMessage, setHotelSaveStatusMessage] = useState('');
  const [hotelSaveStatusOpen, setHotelSaveStatusOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [useColoredCategories, setUseColoredCategories] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [randomIcon] = useState(getRandomIcon());
  const [recentlyViewedHotels, setRecentlyViewedHotels] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    return savedCollapsed !== null ? JSON.parse(savedCollapsed) : false;
  });
  const [showArchivedHotels, setShowArchivedHotels] = useState(false);
  const { updateAvailable, handleUpdateConfirm, handleUpdateLater } = useVersionCheck();
  // Add snackbar state
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success',
    autoHideDuration: 4000 
  });
  const [jakobDialog, setJakobDialog] = useState({ 
    open: false, 
    message: '', 
    step: 0 
  });
  // Add these new state variables near other state declarations
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const selectedHotelListenerRef = useRef(null);
  // Add this near other state declarations
  const debouncedUpdateRef = useRef(null);
  // Add these near other state declarations
  const hotelsMapRef = useRef(new Map());
  // Track programmatic navigation to prevent double-updates
  const isNavigatingRef = useRef(false);
  // Current pathname so debounced search can avoid auto-navigating when user has already left
  const pathnameRef = useRef('/app');

  const api = useApi();
  const { user, signOut } = useAuth();  // Add signOut to the destructured values
  const { categories } = useCategories();
  const { trackPageView, trackUserAction, trackLogin, trackHotelVisit, trackHotelSearch, trackSessionStart } = useAnalytics();
  
  // Apply emoji cursor when user is on /app routes
  useEmojiCursor(user);
  const location = useLocation();
  const navigate = useNavigate();
  pathnameRef.current = location.pathname;

  // Track user sessions and login events
  useEffect(() => {
    if (user) {
      trackLogin('google');
      trackSessionStart();
    }
  }, [user, trackLogin, trackSessionStart]);

  // Track page views when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    if (user && currentPath !== '/login') {
      trackPageView(currentPath, {
        hotel_context: selectedHotel?.id || null,
        user_authenticated: true,
        search_active: !!searchQuery,
        filters_active: Object.keys(filters).length > 0
      });
    }
  }, [location.pathname, user, trackPageView, selectedHotel?.id, searchQuery, filters]);

  // Memoize searchable fields computation
  const searchableFields = useMemo(() => 
    categories.flatMap(group => 
      group.items
        .filter(item => !item.isArchived && item.type !== 'password')
        .map(item => item.fieldName)
    ),
    [categories]
  );

  // Memoize field definitions for better performance
  const fieldDefinitions = useMemo(() => {
    const defs = new Map();
    categories.forEach(group => {
      group.items.forEach(item => {
        defs.set(item.fieldName, item);
      });
    });
    return defs;
  }, [categories]);

  // Optimize hotel filtering with memoization
  const filterHotels = useCallback((searchTerm, filters) => {
    if (!hotels.length) return [];
    
    let results = hotels;

    // Apply search if exists
    if (searchTerm) {
      const searchLower = getNormalizedString(searchTerm.trim());
      
      // Create a Set of matching hotel IDs for better performance
      const matchingIds = new Set();
      
      results.forEach(hotel => {
        // Early return if already matched
        if (matchingIds.has(hotel.id)) return;

        // Search in all searchable category fields
        for (const fieldName of searchableFields) {
          const value = hotel?.categoryData?.[`category_${fieldName.toLowerCase()}`]?.value;
          if (value && getNormalizedString(String(value)).includes(searchLower)) {
            matchingIds.add(hotel.id);
            return;
          }
        }

        // Search in direct hotel fields including address
        if (
          (hotel.name && getNormalizedString(String(hotel.name)).includes(searchLower)) ||
          (hotel.notes && getNormalizedString(String(hotel.notes)).includes(searchLower)) ||
          (hotel.categoryData?.category_address?.value && 
           getNormalizedString(String(hotel.categoryData.category_address.value)).includes(searchLower))
        ) {
          matchingIds.add(hotel.id);
          return;
        }

        // Search in tags
        if (Array.isArray(hotel.tags)) {
          for (const tag of hotel.tags) {
            if (tag !== null && tag !== undefined && getNormalizedString(String(tag)).includes(searchLower)) {
              matchingIds.add(hotel.id);
              return;
            }
          }
        }
      });

      results = results.filter(hotel => matchingIds.has(hotel.id));
    }

    // Apply filters if any are active
    if (filters && Object.keys(filters).length > 0) {
      for (const [field, selectedValues] of Object.entries(filters)) {
        if (!selectedValues || selectedValues.length === 0) continue;

        // Handle special 'textSearch' filter similar to the main search
        if (field === 'textSearch') {
          results = results.filter(hotel => {
            // A hotel must match ALL search terms, not just one
            return selectedValues.every(searchTerm => {
              const searchLower = getNormalizedString(searchTerm.trim());
              
              // Create a variable to track if this term matched anything in the hotel
              let termMatched = false;
              
              // Search in all searchable category fields
              for (const fieldName of searchableFields) {
                const value = hotel?.categoryData?.[`category_${fieldName.toLowerCase()}`]?.value;
                if (value && getNormalizedString(String(value)).includes(searchLower)) {
                  termMatched = true;
                  break;
                }
              }
              
              // Search in direct hotel fields including address
              if (!termMatched && (
                (hotel.name && getNormalizedString(String(hotel.name)).includes(searchLower)) ||
                (hotel.notes && getNormalizedString(String(hotel.notes)).includes(searchLower)) ||
                (hotel.categoryData?.category_address?.value && 
                 getNormalizedString(String(hotel.categoryData.category_address.value)).includes(searchLower))
              )) {
                termMatched = true;
              }
              
              // Search in tags
              if (!termMatched && Array.isArray(hotel.tags)) {
                for (const tag of hotel.tags) {
                  if (tag !== null && tag !== undefined && getNormalizedString(String(tag)).includes(searchLower)) {
                    termMatched = true;
                    break;
                  }
                }
              }
              
              // This search term matches if any field in the hotel contains it
              return termMatched;
            });
          });
          
          continue; // Skip to next filter
        }

        // Handle special 'status' filter
        // Status is a direct property of the hotel object (not in categoryData)
        // This filter lets users filter between Active and WIP hotels while
        // still keeping other category filters available
        if (field === 'status') {
          results = results.filter(hotel => {
            return hotel.status && selectedValues.includes(hotel.status);
          });
          continue; // Skip to next filter
        }

        const fieldDef = fieldDefinitions.get(field);
        if (!fieldDef) continue;

        results = results.filter(hotel => {
          const value = hotel?.categoryData?.[`category_${field.toLowerCase()}`]?.value;

          switch (fieldDef.type) {
            case 'checkbox':
              // Handle checkbox fields - check if value is true (checked)
              let isChecked = false;
              if (value === true) {
                isChecked = true;
              } else if (value !== false && value !== null && value !== undefined && value !== '') {
                const strValue = String(value).toLowerCase().trim();
                isChecked = strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
              }
              // When checkbox filter is active, only return hotels where checkbox is checked
              // Filter value should be 'true' or true
              if (selectedValues.includes('true') || selectedValues.includes(true)) {
                return isChecked;
              }
              // This shouldn't happen if filter is active, but return false as fallback
              return false;

            case 'email':
              if (selectedValues.includes('has_email')) return value?.includes('@');
              if (selectedValues.includes('no_email')) return !value?.includes('@');
              break;

            case 'url':
              if (selectedValues.includes('has_url')) {
                return value && (value.startsWith('http') || value.startsWith('www'));
              }
              if (selectedValues.includes('no_url')) {
                return !value || (!value.startsWith('http') && !value.startsWith('www'));
              }
              break;

            default:
              if (selectedValues.includes('present')) {
                return value && !['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(value.toLowerCase());
              }
              if (selectedValues.includes('absent')) {
                return !value || ['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(value.toLowerCase());
              }
              return value && selectedValues.includes(value);
          }
          return false;
        });
      }
    }

    return results;
  }, [hotels, searchableFields, fieldDefinitions]);

  // Optimize debounced search with proper cleanup
  const debouncedSearch = useMemo(() => {
    const debouncedFn = debounce((term, filters) => {
      try {
        const filtered = filterHotels(term, filters);
        setFilteredHotels(filtered);
        
        // Track search analytics - KEY METRIC
        if (term && term.trim()) {
          trackHotelSearch(term.trim(), filtered.length, {
            active_filters: Object.keys(filters || {}).filter(key => filters[key]?.length > 0),
            filter_count: Object.keys(filters || {}).reduce((count, key) => count + (filters[key]?.length || 0), 0),
            total_hotels: hotels.length,
            search_timestamp: new Date().toISOString(),
            has_results: filtered.length > 0,
            result_percentage: hotels.length > 0 ? (filtered.length / hotels.length * 100).toFixed(1) : 0,
            auto_selected: filtered.length === 1 && !isEditing,
            search_length: term.trim().length
          });
        }
        
        // Only auto-select if exactly one result, not editing, AND user is still on hotel list/detail.
        // If they've already navigated away (e.g. to /app/documentation), the debounce can fire
        // late and would override their navigation — so skip auto-navigate when path isn't /app or /app/hotel/...
        const currentPath = pathnameRef.current || '';
        const stillOnHotelView = currentPath === '/app' || /^\/app\/hotel\/[^/]+$/.test(currentPath);
        if (filtered.length === 1 && !isEditing && stillOnHotelView) {
          const autoSelectedHotel = filtered[0];
          setSelectedHotel(autoSelectedHotel);
          setIsAutoSelected(true);
          
          // Mark that we're navigating programmatically
          isNavigatingRef.current = true;
          
          // Update URL with property number
          const propertyNumber = autoSelectedHotel.id || 
                               autoSelectedHotel.propertyNumber || 
                               autoSelectedHotel.categoryData?.category_propertynumber?.value;
          if (propertyNumber) {
            navigate(`/app/hotel/${propertyNumber}`, { replace: true });
          }
          
          // Reset the flag after a short delay
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 100);
          
          // Track auto-selection
          const hotelName = autoSelectedHotel.name || autoSelectedHotel.categoryData?.category_name?.value || 'Unknown Hotel';
          trackHotelVisit(autoSelectedHotel.id, hotelName, {
            source: 'auto_selection',
            search_term: term.trim(),
            selection_method: 'auto_single_result'
          });
        } else if (filtered.length === 1 && isEditing) {
          // Show warning but don't auto-select if in edit mode
          console.log('Not auto-selecting hotel because in edit mode');
        }
      } finally {
        setFilterLoading(false);
      }
    }, 150);

    return debouncedFn;
  }, [filterHotels, isEditing, trackHotelSearch, hotels.length, trackHotelVisit, navigate]);

  // Cleanup debounced search on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      normalizedCache.clear();
    };
  }, [debouncedSearch]);

  // Search handler
  const handleSearchChange = useCallback((event) => {
    const value = event?.target?.value ?? '';
    
    setSearchQuery(value);
    
    if (!value.trim()) {
      setFilteredHotels(hotels);
      
      // Don't touch selection at all if in edit mode
      if (!isEditing && selectedHotel) {
        setSelectedHotel(null);
        setIsAutoSelected(false);
        isNavigatingRef.current = true;
        navigate('/app', { replace: true });
        setTimeout(() => { isNavigatingRef.current = false; }, 100);
      } else if (isEditing && value === '') {
        // Only show warning if user explicitly cleared the search while in edit mode
        // Not when entering edit mode
        console.log('Prevented closing hotel because in edit mode');
        // Optional warning (comment this out if it still appears incorrectly)
        // setSnackbar({
        //   open: true,
        //   message: `Can't clear selection while editing. Save or cancel your changes first.`,
        //   severity: 'warning',
        //   autoHideDuration: 5000
        // });
      }
      return;
    }
    
    setFilterLoading(true);
    debouncedSearch(value, filters);
  }, [hotels, filters, debouncedSearch, isEditing, selectedHotel, navigate]);

  // Filter handler
  const handleFilterChange = useCallback((field, values) => {
    const newFilters = { ...filters };
    if (values.length === 0) {
      delete newFilters[field];
    } else {
      newFilters[field] = values;
    }
    setFilters(newFilters);
    
    // Apply the filters and update the filtered hotels
    setFilterLoading(true);
    const filtered = filterHotels(searchQuery, newFilters);
    setFilteredHotels(filtered);
    setFilterLoading(false);
    
    // Track filter usage analytics
    trackUserAction('filter_changed', field, {
      filter_field: field,
      filter_values: values,
      filter_count: values.length,
      total_active_filters: Object.keys(newFilters).length,
      result_count: filtered.length,
      has_search_query: !!searchQuery,
      search_query: searchQuery || null
    });
  }, [filters, searchQuery, filterHotels, trackUserAction]);

  // Effect to update filtered results when hotels or categories change
  useEffect(() => {
    if (searchQuery || Object.keys(filters).some(key => filters[key].length > 0)) {
      debouncedSearch(searchQuery, filters);
    } else {
      setFilteredHotels(hotels);
    }
  }, [hotels, categories, debouncedSearch, searchQuery, filters]);

  // Handle hotel selection
  const handleHotelSelect = useCallback((hotel) => {
    // Check if user is in edit mode before allowing hotel navigation
    if (isEditing) {
      const hotelName = selectedHotel?.name || selectedHotel?.categoryData?.category_name?.value || 'this hotel';
      setSnackbar({
        open: true,
        message: `Oops! You have unsaved changes for ${hotelName}. Save or cancel your edits before exploring other hotels.`,
        severity: 'warning',
        autoHideDuration: 5000
      });
      return;
    }
    
    const newSelectedHotel = selectedHotel?.id === hotel?.id ? null : hotel;
    setSelectedHotel(newSelectedHotel);
    setIsAutoSelected(false); // Manual selection, reset auto-select flag
    
    // Mark that we're navigating programmatically to prevent useEffect from re-selecting
    isNavigatingRef.current = true;
    
    // Update URL with property number
    if (newSelectedHotel) {
      const propertyNumber = newSelectedHotel.id || 
                           newSelectedHotel.propertyNumber || 
                           newSelectedHotel.categoryData?.category_propertynumber?.value;
      if (propertyNumber) {
        // Use replace: false to add history entry so back button works
        navigate(`/app/hotel/${propertyNumber}`, { replace: false });
      }
    } else {
      // If deselecting hotel, go back to /app
      navigate('/app', { replace: false });
    }
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
    
    // Track hotel visit analytics - KEY METRIC
    if (newSelectedHotel) {
      const hotelName = newSelectedHotel.name || newSelectedHotel.categoryData?.category_name?.value || 'Unknown Hotel';
      const chainValue = newSelectedHotel.categoryData?.category_chain?.value || 'Independent';
      
      trackHotelVisit(newSelectedHotel.id, hotelName, {
        source: 'hotel_list',
        search_term: searchQuery || null,
        chain: chainValue,
        hotel_status: newSelectedHotel.status || 'unknown',
        has_active_filters: Object.keys(filters).some(key => filters[key].length > 0),
        filter_count: Object.keys(filters).reduce((count, key) => count + (filters[key]?.length || 0), 0),
        total_hotels_available: hotels.length,
        filtered_hotels_count: filteredHotels.length
      });
      
      // Also track this as a general user action
      trackUserAction('hotel_selected', hotelName, {
        hotel_id: newSelectedHotel.id,
        selection_method: 'click'
      });
    }
  }, [isEditing, selectedHotel, trackHotelVisit, trackUserAction, searchQuery, filters, hotels.length, filteredHotels.length, navigate]);

  // Handle compact view toggle
  const handleCompactViewToggle = useCallback(async () => {
    if (!user) return;
    
    try {
      const newCompactView = !user?.themePreferences?.compactView;
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'themePreferences.compactView': newCompactView,
        updatedAt: new Date(),
        updatedBy: user.email
      });
      
      // Track feature usage
      trackUserAction('view_toggle', 'compact_view', {
        new_view: newCompactView ? 'compact' : 'regular',
        previous_view: newCompactView ? 'regular' : 'compact'
      });
      
      setSnackbar({
        open: true,
        message: `Switched to ${newCompactView ? 'compact' : 'regular'} view`,
        severity: 'success',
        autoHideDuration: 2000
      });
    } catch (error) {
      console.error('Error updating compact view preference:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update view preference',
        severity: 'error',
        autoHideDuration: 3000
      });
    }
  }, [user, setSnackbar, trackUserAction]);

  // Create theme using the centralized theme engine
  const theme = useMemo(() => {
    const variant = darkMode ? 'dark' : 'light';
    const customColors = {
      primary: pinkMode ? '#FF1493' : (user?.themePreferences?.primaryColor || '#FF4D4F'),
      secondary: pinkMode ? '#FF69B4' : (user?.themePreferences?.secondaryColor || '#CE93D8'),
    };

    // Handle pink mode special case
    if (pinkMode) {
      const pinkTheme = createAppTheme({ 
        variant, 
        customColors,
        pinkMode: true // Special flag for pink mode
      });
      
      // Override specific pink mode colors
      pinkTheme.palette.background.default = '#1A0011';
      pinkTheme.palette.background.paper = '#260019';
      pinkTheme.palette.text.primary = '#FFB6C1';
      pinkTheme.palette.text.secondary = '#FF69B4';
      pinkTheme.palette.divider = 'rgba(255, 20, 147, 0.12)';
      pinkTheme.palette.pink = {
        main: '#FF1493',
        light: '#FF69B4',
        dark: '#C71585',
        contrastText: '#FFB6C1',
      };
      
      return pinkTheme;
    }

    return createAppTheme({ variant, customColors });
  }, [darkMode, pinkMode, user?.themePreferences?.primaryColor, user?.themePreferences?.secondaryColor]);

  // Load dark mode preference from cookie
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setDarkMode(JSON.parse(savedMode));
    }
    initializeTheme();
    setThemeLoaded(true);
  }, []);

  // Add edit mode handlers
  const handleEditClick = useCallback(() => {
    console.log('Edit clicked - resetting auto-selection and entering edit mode');
    setHotelSaveStatusOpen(false); // Animate out saved chip if visible
    // Important: Immediately set both flags in one update
    setIsEditing(true);
    setIsAutoSelected(false); // Always reset this when entering edit mode
    setEditedHotel({ ...selectedHotel }); // Create a copy for editing
  }, [selectedHotel]);

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHotel(null);
  };

  // Update handleSaveEdit to work with real-time updates
  const handleSaveEdit = useCallback(async () => {
    console.log('⭐⭐⭐ handleSaveEdit called with:', { editedHotel, selectedHotel });
    try {
      setIsSaving(true);
      
      // Get only the changes, not the entire object
      const updatedFields = {
        categoryData: editedHotel.categoryData
      };
      
      // Add non-category fields if they exist in editedHotel
      ['notes', 'tags', 'email', 'phone', 'address'].forEach(field => {
        if (editedHotel[field] !== undefined) {
          updatedFields[field] = editedHotel[field];
        }
      });
      
      console.log('⭐⭐⭐ Prepared updatedFields:', updatedFields);
      
      // Create full hotel object for UI updates
      const fullUpdatedHotel = {
        ...selectedHotel,
        ...updatedFields,
        categoryData: {
          ...selectedHotel.categoryData,
          ...updatedFields.categoryData
        }
      };

      // Optimistically update UI
      setHotels(prevHotels => 
        prevHotels.map(h => h.id === fullUpdatedHotel.id ? fullUpdatedHotel : h)
      );
      setFilteredHotels(prevHotels => 
        prevHotels.map(h => h.id === fullUpdatedHotel.id ? fullUpdatedHotel : h)
      );
      setSelectedHotel(fullUpdatedHotel);
      
      console.log('⭐⭐⭐ About to call api.updateHotel with:', editedHotel.id, updatedFields);
      
      // Save only the changes to Firestore
      const response = await api.updateHotel(editedHotel.id, updatedFields);
      
      console.log('⭐⭐⭐ api.updateHotel response:', response);
      
      if (response.success) {
        setIsEditing(false);
        setEditedHotel(null);
        
        setHotelSaveStatus('success');
        setHotelSaveStatusOpen(true);
        setTimeout(() => setHotelSaveStatusOpen(false), 2500);
      } else {
        throw new Error('Failed to save changes');
      }
    } catch (error) {
      console.error('⭐⭐⭐ Error saving changes:', error);
      
      // Revert changes if save failed
      if (selectedHotel) {
        setHotels(prevHotels => 
          prevHotels.map(h => h.id === selectedHotel.id ? selectedHotel : h)
        );
        setFilteredHotels(prevHotels => 
          prevHotels.map(h => h.id === selectedHotel.id ? selectedHotel : h)
        );
        setSelectedHotel(selectedHotel);
      }
      
      setHotelSaveStatus('error');
      setHotelSaveStatusMessage('Failed to save. Please try again.');
      setHotelSaveStatusOpen(true);
      setTimeout(() => setHotelSaveStatusOpen(false), 4000);
    } finally {
      setIsSaving(false);
    }
  }, [api, editedHotel, selectedHotel]);

  // Update handleEditFieldChange to work with category structure
  const handleEditFieldChange = useCallback((field, value) => {
    setEditedHotel(prev => {
      // Handle notes field specially as it's not part of categoryData
      if (field.toLowerCase() === 'notes') {
        // Skip update if value hasn't changed
        if (JSON.stringify(prev.notes) === JSON.stringify(value)) return prev;
        
        return {
          ...prev,
          notes: value // Preserve the array structure directly
        };
      }
      
      // Regular field handling
      // Skip update if value hasn't changed
      const currentValue = prev.categoryData?.[`category_${field.toLowerCase()}`]?.value;
      if (currentValue === value) return prev;
      
      return {
        ...prev,
        categoryData: {
          ...prev.categoryData,
          [`category_${field.toLowerCase()}`]: {
            value: value,
            updatedAt: new Date(),
            updatedBy: user.email
          }
        }
      };
    });
  }, [user?.email]);

  // Update copy functionality
  const handleCopyClick = (value, itemTitle, itemType) => {
    // Check if this is a phone number and ensure it has the "+" prefix
    let formattedValue = value;
    
    if (itemType === 'telephone' && formattedValue && !formattedValue.startsWith('+')) {
      formattedValue = `+${formattedValue}`;
    }
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(formattedValue).then(() => {
        setSnackbar({
          open: true,
          message: getRandomMessage('copy'),
          severity: 'success',
          autoHideDuration: 2000
        });
      }).catch(() => {
        // Fallback to legacy method
        fallbackCopyTextToClipboard(formattedValue);
      });
    } else {
      // Use fallback method for older browsers
      fallbackCopyTextToClipboard(formattedValue);
    }
  };

  // Fallback clipboard method for older browsers or when clipboard API fails
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setSnackbar({
          open: true,
          message: getRandomMessage('copy'),
          severity: 'success',
          autoHideDuration: 2000
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to copy to clipboard',
          severity: 'error',
          autoHideDuration: 3000
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Copy to clipboard not supported',
        severity: 'error',
        autoHideDuration: 3000
      });
    }
    
    document.body.removeChild(textArea);
  };

  // Prepare data for charts
  const chainDistribution = useMemo(() => {
    if (!hotels.length) return [];

    const distribution = hotels.reduce((acc, hotel) => {
      const chainData = hotel.categoryData?.category_chain;
      if (chainData?.value) {
        const chainName = getChainDisplayName(chainData.value);
        acc[chainName] = (acc[chainName] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(distribution)
      .map(([name, value]) => ({
        name,
        value,
        percent: value / hotels.length
      }))
      .sort((a, b) => b.value - a.value);
  }, [hotels]);

  const systemUsage = useMemo(() => {
    if (!hotels.length || !categories.length) return [];
    
    // Get all categories and their items
    const systemData = categories.map(group => {
      // Get all non-archived items from the group that are enabled for statistics
      const items = group.items
        .filter(item => !item.isArchived && item.type === 'text' && item.showInStatistics)
        .map(item => {
          // Calculate usage for this item
          const values = hotels.reduce((acc, hotel) => {
            const fieldNameLower = item.fieldName.toLowerCase();
            const categoryValue = hotel[fieldNameLower] !== undefined 
              ? hotel[fieldNameLower]
              : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
            if (categoryValue && categoryValue.toLowerCase() !== 'n/a' && categoryValue !== '-') {
              acc[categoryValue] = (acc[categoryValue] || 0) + 1;
            }
            return acc;
          }, {});

          return {
            system: item.title,
            group: group.title,
            values: Object.entries(values)
              .map(([name, count]) => ({
                name,
                count
              }))
              .sort((a, b) => b.count - a.count)
          };
        })
        .filter(item => item.values.length > 0); // Only include items that have values

      return items;
    }).flat(); // Flatten the array of arrays

    // Sort by group order (using category order) and then by system name
    return systemData.sort((a, b) => {
      // Get the original category groups to determine their order
      const groupA = categories.findIndex(cat => cat.title === a.group);
      const groupB = categories.findIndex(cat => cat.title === b.group);
      
      // First sort by group order
      if (groupA !== groupB) return groupA - groupB;
      
      // Then sort by system name within the same group
      return a.system.localeCompare(b.system);
    });
  }, [hotels, categories]); // Removed getCategoryValue from dependencies


  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Add keyboard shortcut listener
  useEffect(() => {
    let pKeyPresses = [];
    const pKeyTimeout = 1000; // 1 second window for 3 presses

    const handleKeyDown = (e) => {
      // Check if we're in an input or contentEditable element
      const isInInput = e.target.isContentEditable || 
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);

      // Pink mode: 3 quick presses of 'p' key
      if (e.key.toLowerCase() === 'p' && !isInInput) {
        const now = Date.now();
        pKeyPresses = pKeyPresses.filter(time => now - time < pKeyTimeout);
        pKeyPresses.push(now);
        
        if (pKeyPresses.length >= 3) {
          e.preventDefault();
          pKeyPresses = []; // Reset the counter
          setPinkMode(prev => {
          if (!prev) {
            const colors = [
              '#FF1493', // Deep Pink
              '#FF69B4', // Hot Pink
              '#FFB6C1', // Light Pink
              '#C71585', // Medium Violet Red
              '#DB7093', // Pale Violet Red
              '#FF82AB', // Pale Pink
              '#FFD7E9', // Light Pink Glow
              '#FF007F', // Rose
              '#FF66B2', // Bright Pink
            ];
            
            // Initial dramatic burst
            confetti({
              particleCount: 250,
              spread: 360,
              origin: { y: 0.6 },
              colors: colors,
              gravity: 0.7,
              scalar: 2.5,
              ticks: 300,
              shapes: ['star', 'circle'],
              drift: 1
            });

            // Create a sequence of effects
            const sequence = [
              // Dramatic side bursts with increased intensity
              () => {
                for (let i = 0; i < 4; i++) {
                  setTimeout(() => {
                    // Left burst
                    confetti({
                      particleCount: 80,
                      angle: 60,
                      spread: 90,
                      origin: { x: 0, y: 0.5 },
                      colors: colors,
                      shapes: ['star'],
                      gravity: 0.2,
                      scalar: 2,
                      drift: 2,
                      ticks: 400
                    });
                    // Right burst
                    confetti({
                      particleCount: 80,
                      angle: 120,
                      spread: 90,
                      origin: { x: 1, y: 0.5 },
                      colors: colors,
                      shapes: ['star'],
                      gravity: 0.2,
                      scalar: 2,
                      drift: -2,
                      ticks: 400
                    });
                  }, i * 250);
                }
              },
              // Double spiral effect
              () => {
                const particleCount = 120;
                const duration = 2000;
                const interval = duration / particleCount;
                
                for(let i = 0; i < particleCount; i++) {
                  setTimeout(() => {
                    // Inner spiral
                    const angleInner = (i / particleCount) * 720;
                    const xInner = 0.5 + Math.cos(angleInner * Math.PI / 180) * 0.3;
                    const yInner = 0.5 + Math.sin(angleInner * Math.PI / 180) * 0.3;
                    
                    confetti({
                      particleCount: 2,
                      angle: angleInner,
                      spread: 20,
                      origin: { x: xInner, y: yInner },
                      colors: [colors[i % colors.length]],
                      gravity: 0.3,
                      scalar: 1.5,
                      ticks: 200,
                      shapes: ['star']
                    });

                    // Outer spiral
                    const angleOuter = (i / particleCount) * 720 + 180;
                    const xOuter = 0.5 + Math.cos(angleOuter * Math.PI / 180) * 0.4;
                    const yOuter = 0.5 + Math.sin(angleOuter * Math.PI / 180) * 0.4;
                    
                    confetti({
                      particleCount: 2,
                      angle: angleOuter,
                      spread: 20,
                      origin: { x: xOuter, y: yOuter },
                      colors: [colors[(i + 4) % colors.length]],
                      gravity: 0.3,
                      scalar: 1.5,
                      ticks: 200,
                      shapes: ['circle']
                    });
                  }, i * interval);
                }
              },
              // Explosive corner bursts
              () => {
                const corners = [
                  { x: 0.1, y: 0.1, angle: 45 },
                  { x: 0.9, y: 0.1, angle: 135 },
                  { x: 0.1, y: 0.9, angle: -45 },
                  { x: 0.9, y: 0.9, angle: -135 }
                ];
                
                corners.forEach((corner, i) => {
                  setTimeout(() => {
                    confetti({
                      particleCount: 60,
                      angle: corner.angle,
                      spread: 80,
                      origin: { x: corner.x, y: corner.y },
                      colors: colors,
                      gravity: 0.2,
                      scalar: 2,
                      ticks: 450,
                      shapes: ['star', 'circle']
                    });
                  }, i * 200);
                });
              }
            ];

            // Execute initial sequence with dramatic timing
            sequence.forEach((effect, index) => {
              setTimeout(effect, index * 800);
            });

            // Enhanced dramatic messages
            const messages = [
              "🎀 INITIALIZING JAKOB MODE... 🎀",
              "✨ CHANNELING PINK ENERGY... ✨",
              "💖 WELCOME TO THE MAGICAL PINK DIMENSION! 💖"
            ];

            // Start the message sequence
            setJakobDialog({ open: true, message: messages[0], step: 0 });

            // Schedule the message changes
            messages.slice(1).forEach((message, index) => {
              setTimeout(() => {
                setJakobDialog(prev => ({
                  ...prev,
                  message,
                  step: index + 1
                }));
              }, (index + 1) * 3000);
            });

            // Grand finale sequence after messages
            setTimeout(() => {
              // Massive central burst
              confetti({
                particleCount: 300,
                spread: 360,
                origin: { x: 0.5, y: 0.5 },
                colors: colors,
                gravity: 0.3,
                scalar: 3,
                ticks: 400,
                shapes: ['star', 'circle'],
                drift: 0
              });

              // Rapid-fire side bursts
              for(let i = 0; i < 8; i++) {
                setTimeout(() => {
                  // Left side
                  confetti({
                    particleCount: 40,
                    angle: 45 + (i * 10),
                    spread: 50,
                    origin: { x: 0, y: 0.5 },
                    colors: colors,
                    gravity: 0.2,
                    scalar: 2,
                    ticks: 300
                  });
                  // Right side
                  confetti({
                    particleCount: 40,
                    angle: 135 - (i * 10),
                    spread: 50,
                    origin: { x: 1, y: 0.5 },
                    colors: colors,
                    gravity: 0.2,
                    scalar: 2,
                    ticks: 300
                  });
                }, i * 100);
              }

              // Upward shooting stars
              for(let i = 0; i < 5; i++) {
                setTimeout(() => {
                  confetti({
                    particleCount: 30,
                    angle: 90,
                    spread: 30,
                    origin: { x: 0.2 + (i * 0.15), y: 1 },
                    colors: colors,
                    gravity: 0.6,
                    scalar: 1.5,
                    ticks: 400,
                    shapes: ['star']
                  });
                }, i * 150);
              }

              // Final sparkle shower
              setTimeout(() => {
                for(let i = 0; i < 3; i++) {
                  setTimeout(() => {
                    confetti({
                      particleCount: 100,
                      spread: 180,
                      origin: { y: -0.2 },
                      colors: colors,
                      gravity: 0.4,
                      scalar: 2,
                      ticks: 300,
                      shapes: ['circle', 'star']
                    });
                  }, i * 200);
                }
              }, 800);
            }, messages.length * 3000); // Start finale after messages

            // Close the dialog after all effects
            setTimeout(() => {
              setJakobDialog(prev => ({ ...prev, open: false }));
            }, messages.length * 3000);
          }
          return !prev;
        });
        return;
        }
      }

      // Tab handler for search focus - different search bars based on sidebar state
      if (e.key === 'Tab' && !isInInput) {
        e.preventDefault();
        
        if (sidebarCollapsed) {
          // When sidebar is collapsed, focus the search in NoHotelSelectedCampaign
          const campaignSearchInput = document.querySelector('[data-campaign-search]');
          if (campaignSearchInput) {
            campaignSearchInput.focus();
            if (searchQuery) {
              campaignSearchInput.select();
            }
          }
        } else {
          // When sidebar is expanded, focus the search in HotelList
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            if (searchQuery) {
              searchInputRef.current.select();
            }
          }
        }
        return;
      }

      // Ctrl + F for filter dialog
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setFilterDialogOpen(true);
        return;
      }

      // ESC - Clear selection or close dialogs
      if (e.key === 'Escape') {
        if (statsDialogOpen) {
          setStatsDialogOpen(false);
        } else if (isEditing) {
          handleCancelEdit();
        } else if (selectedHotel) {
          setSelectedHotel(null);
          setSearchQuery(''); // Clear search when closing hotel
          isNavigatingRef.current = true;
          navigate('/app', { replace: true });
          setTimeout(() => { isNavigatingRef.current = false; }, 100);
        }
        return;
      }

      // Ctrl + E - Start editing current hotel
      if (e.ctrlKey && e.key === 'e' && selectedHotel && !isEditing) {
        e.preventDefault();
        handleEditClick();
        return;
      }

      // Ctrl + S - Save changes when editing
      if (e.ctrlKey && e.key === 's' && isEditing) {
        e.preventDefault();
        handleSaveEdit();
        return;
      }

      // Arrow keys for hotel navigation (when not in input/textarea)
      if (!isInInput) {
        if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !isEditing) {
          e.preventDefault();
          const currentIndex = filteredHotels.findIndex(h => h.id === selectedHotel?.id);
          let newIndex;
          
          if (e.key === 'ArrowUp') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : filteredHotels.length - 1;
          } else {
            newIndex = currentIndex < filteredHotels.length - 1 ? currentIndex + 1 : 0;
          }
          
          handleHotelSelect(filteredHotels[newIndex]);
        } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && isEditing) {
          // Prevent default to avoid unwanted scrolling, but don't navigate
          e.preventDefault();
        }

        // Enter to select first hotel if none selected
        if (e.key === 'Enter' && !selectedHotel && filteredHotels.length > 0 && !isEditing) {
          handleHotelSelect(filteredHotels[0]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery, selectedHotel, isEditing, filteredHotels, statsDialogOpen, handleHotelSelect, handleEditClick, handleSaveEdit, setSnackbar, api, sidebarCollapsed, navigate]);

  const handleTagsChange = async (hotelId, newTags) => {
    try {
      // Find the hotel
      const hotel = filteredHotels.find(h => h.id === hotelId);
      if (!hotel) return;

      // Create updated hotel object with the new tags
      const updatedHotel = { ...hotel, tags: newTags };

      // Optimistically update UI
      setHotels(prevHotels => 
        prevHotels.map(h => h.id === hotelId ? updatedHotel : h)
      );

      setFilteredHotels(prevHotels => 
        prevHotels.map(h => h.id === hotelId ? updatedHotel : h)
      );

      if (selectedHotel?.id === hotelId) {
        setSelectedHotel(updatedHotel);
      }

      // Save to server using Firestore directly
      const hotelRef = doc(db, 'hotels', hotelId);
      await updateDoc(hotelRef, {
        tags: newTags,
        updatedAt: new Date(),
        updatedBy: user.email
      });

      console.log('Tags saved successfully:', newTags);
    } catch (error) {
      console.error('Error saving tags:', error);
      
      // Revert changes if save failed
      const originalHotel = filteredHotels.find(h => h.id === hotelId);
      
      setHotels(prevHotels => 
        prevHotels.map(h => h.id === hotelId ? originalHotel : h)
      );

      setFilteredHotels(prevHotels => 
        prevHotels.map(h => h.id === hotelId ? originalHotel : h)
      );

      if (selectedHotel?.id === hotelId) {
        setSelectedHotel(originalHotel);
      }
    }
  };

  // Add this near other useEffect hooks
  useEffect(() => {
    const savedColorSetting = localStorage.getItem('useColoredCategories');
    if (savedColorSetting !== null) {
      setUseColoredCategories(JSON.parse(savedColorSetting));
    }
  }, []);

  const isLoginPage = location.pathname === '/login';

  // Add useEffect to persist dark mode changes
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Add useEffect to persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Create filter options for a field
  const createFilterOptions = useCallback((fieldName, currentFilters = {}) => {
    // Get the field definition from categories
    const field = categories.flatMap(group => group.items).find(item => item.fieldName === fieldName);
    if (!field || !field.showInFilters) return []; // Only show fields that have showInFilters enabled

    // When we have a text search filter active, we need a special approach
    const hasTextSearch = currentFilters.textSearch && currentFilters.textSearch.length > 0;
    
    // For generating filter options, we need to handle custom text search differently
    const filtersForOptions = { ...currentFilters };
    delete filtersForOptions[fieldName]; // Remove current field to avoid circular dependency
    
    // Step 1: Filter hotels based on all filters EXCEPT text search and current field
    // This gives us the initial set of hotels that match other category filters
    const filtersWithoutText = { ...filtersForOptions };
    if (hasTextSearch) delete filtersWithoutText.textSearch;
    
    const preFilteredHotels = hotels.filter(hotel => {
      return Object.entries(filtersWithoutText).every(([filterField, filterValues]) => {
        if (!filterValues || filterValues.length === 0) return true;
        
        // Special handling for status filter
        if (filterField === 'status') {
          return hotel.status && filterValues.includes(hotel.status);
        }
        
        // Get value from hotel data
        const fieldNameLower = filterField.toLowerCase();
        const value = hotel[fieldNameLower] !== undefined 
          ? hotel[fieldNameLower]
          : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
        
        // Handle special filter cases
        const fieldDef = categories.flatMap(group => group.items).find(item => item.fieldName === filterField);
        if (!fieldDef) return false;
        
        // Handle checkbox fields
        if (fieldDef.type === 'checkbox') {
          // For checkbox, check if value is true (checked)
          // Handle various representations of true/false
          let isChecked = false;
          if (value === true) {
            isChecked = true;
          } else if (value !== false && value !== null && value !== undefined && value !== '') {
            const strValue = String(value).toLowerCase().trim();
            isChecked = strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
          }
          // Filter value should be 'true' or true
          return filterValues.includes('true') || filterValues.includes(true) ? isChecked : !isChecked;
        }
        
        if (!value) return false;
        
        if (fieldDef.type === 'boolean' || filterValues.includes('present') || filterValues.includes('absent')) {
          if (filterValues.includes('present')) {
            return value && !['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(value.toLowerCase());
          }
          if (filterValues.includes('absent')) {
            return !value || ['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(value.toLowerCase());
          }
        }
        
        if (fieldDef.type === 'email') {
          if (filterValues.includes('has_email')) return value?.includes('@');
          if (filterValues.includes('no_email')) return !value?.includes('@');
        }
        
        if (fieldDef.type === 'url') {
          if (filterValues.includes('has_url')) {
            return value && (value.startsWith('http') || value.startsWith('www'));
          }
          if (filterValues.includes('no_url')) {
            return !value || (!value.startsWith('http') && !value.startsWith('www'));
          }
        }
        
        // Default case: check if value is in the filter values
        return filterValues.includes(value);
      });
    });
    
    // Step 2: If we have a text search filter, apply it to get hotels that match BOTH text search and other filters
    let textFilteredHotels = [...preFilteredHotels];
    if (hasTextSearch) {
      textFilteredHotels = preFilteredHotels.filter(hotel => {
        // For text search, a hotel must match ALL search terms
        return currentFilters.textSearch.every(searchTerm => {
          const searchLower = getNormalizedString(searchTerm.trim());
          let termMatched = false;
          
          // Search in all searchable category fields
          for (const searchField of searchableFields) {
            const value = hotel?.categoryData?.[`category_${searchField.toLowerCase()}`]?.value;
            if (value && getNormalizedString(String(value)).includes(searchLower)) {
              termMatched = true;
              break;
            }
          }
          
          // Search in direct hotel fields
          if (!termMatched && (
            (hotel.name && getNormalizedString(String(hotel.name)).includes(searchLower)) ||
            (hotel.notes && getNormalizedString(String(hotel.notes)).includes(searchLower))
          )) {
            termMatched = true;
          }
          
          // Search in tags
          if (!termMatched && Array.isArray(hotel.tags)) {
            for (const tag of hotel.tags) {
              if (tag !== null && tag !== undefined && getNormalizedString(String(tag)).includes(searchLower)) {
                termMatched = true;
                break;
              }
            }
          }
          
          return termMatched;
        });
      });
    }
    
    // Step 3: Collect unique values for this field from all pre-filtered hotels
    const optionsMap = new Map();
    
    // Check if this is an email or URL field - if so, we'll handle it specially
    const isEmailOrUrl = field.type === 'email' || field.type === 'url';
    
    // Only collect individual values for non-email/URL fields
    if (!isEmailOrUrl) {
      preFilteredHotels.forEach(hotel => {
        const fieldNameLower = fieldName.toLowerCase();
        const value = hotel[fieldNameLower] !== undefined 
          ? hotel[fieldNameLower]
          : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
        if (!value) return;
        
        // Initialize option if it doesn't exist
        if (!optionsMap.has(value)) {
          optionsMap.set(value, {
            value,
            label: value,
            count: 0
          });
        }
        
        // Increment the count for this value
        const option = optionsMap.get(value);
        option.count += 1;
      });
      
      // If text search is active, adjust the counts to only include hotels that match the text search
      if (hasTextSearch) {
        for (const [value, option] of optionsMap.entries()) {
          // Count how many hotels match both this value AND text search
          const fieldNameLower = fieldName.toLowerCase();
          const matchCount = textFilteredHotels.filter(h => {
            const hValue = h[fieldNameLower] !== undefined 
              ? h[fieldNameLower]
              : h.categoryData?.[`category_${fieldNameLower}`]?.value || null;
            return hValue === value;
          }).length;
          option.count = matchCount;
        }
      }
    }
    
    // Step 4: Handle special types (checkbox, boolean, email, url)
    if (field.type === 'checkbox') {
      // For checkbox fields, count hotels where checkbox is checked (true)
      // Use preFilteredHotels (hotels matching other filters) for base count
      let checkedCount = preFilteredHotels.filter(hotel => {
        const fieldNameLower = fieldName.toLowerCase();
        const value = hotel[fieldNameLower] !== undefined 
          ? hotel[fieldNameLower]
          : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
        // Check for various true representations
        if (value === true) return true;
        if (value === false || value === null || value === undefined || value === '') return false;
        const strValue = String(value).toLowerCase().trim();
        return strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
      }).length;
      
      // If text search is active, adjust count to only include hotels matching text search
      if (hasTextSearch) {
        checkedCount = textFilteredHotels.filter(hotel => {
          const fieldNameLower = fieldName.toLowerCase();
          const value = hotel[fieldNameLower] !== undefined 
            ? hotel[fieldNameLower]
            : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
          if (value === true) return true;
          if (value === false || value === null || value === undefined || value === '') return false;
          const strValue = String(value).toLowerCase().trim();
          return strValue === 'true' || strValue === 'yes' || strValue === '1' || strValue === 'checked';
        }).length;
      }
      
      // Always return an option for checkbox, even if count is 0
      optionsMap.set('true', { value: 'true', label: field.title || 'Checked', count: checkedCount });
    } else if (field.type === 'boolean' || isEmailOrUrl) {
      const fieldNameLower = fieldName.toLowerCase();
      const presentCount = textFilteredHotels.filter(hotel => {
        const value = hotel[fieldNameLower] !== undefined 
          ? hotel[fieldNameLower]
          : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
        return value && !['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(String(value).toLowerCase());
      }).length;
      
      const absentCount = textFilteredHotels.filter(hotel => {
        const value = hotel[fieldNameLower] !== undefined 
          ? hotel[fieldNameLower]
          : hotel.categoryData?.[`category_${fieldNameLower}`]?.value || null;
        return !value || ['no', 'false', 'inactive', '-', 'none', 'n/a'].includes(String(value).toLowerCase());
      }).length;
      
      if (field.type === 'boolean') {
        optionsMap.set('present', { value: 'present', label: 'Yes', count: presentCount });
        optionsMap.set('absent', { value: 'absent', label: 'No', count: absentCount });
      } else if (field.type === 'email') {
        optionsMap.set('has_email', { value: 'has_email', label: `Has ${field.title || 'Email'}`, count: presentCount });
        optionsMap.set('no_email', { value: 'no_email', label: `No ${field.title || 'Email'}`, count: absentCount });
      } else if (field.type === 'url') {
        optionsMap.set('has_url', { value: 'has_url', label: `Has ${field.title || 'URL'}`, count: presentCount });
        optionsMap.set('no_url', { value: 'no_url', label: `No ${field.title || 'URL'}`, count: absentCount });
      }
    }
    
    // Step 5: Convert Map to array and sort by count (descending), then alphabetically
    return Array.from(optionsMap.values())
      .filter(option => {
        // For checkbox fields, always include them even if count is 0
        if (field.type === 'checkbox') return true;
        // For other fields, only include options that have matches with text search
        return option.count > 0 || !hasTextSearch;
      })
      .sort((a, b) => {
        // First by count (descending)
        if (b.count !== a.count) return b.count - a.count;
        // Then alphabetically
        return a.label.localeCompare(b.label);
      });
  }, [categories, hotels, searchableFields]);

  // Replace the initial fetch effect
  // Depend on user?.uid (stable) rather than the full user object, which gets a new reference
  // on every Firebase token refresh (~hourly). This prevents unnecessary re-fetches.
  const userUid = user?.uid;
  useEffect(() => {
    if (!userUid) return;
    // #region agent log
    try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'fetchHotelsEffect',path:window.location.pathname,uid:userUid});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
    // #endregion

    const fetchHotels = async () => {
      try {
        // Include archived hotels if toggle is on
        const statusFilter = showArchivedHotels 
          ? ['active', 'work_in_progress', 'archived']
          : ['active', 'work_in_progress'];
        
        const q = query(
          collection(db, 'hotels'),
          where('status', 'in', statusFilter)
        );
        
        const snapshot = await getDocs(q);
        const hotelData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort hotels by property number
        const sortedHotels = hotelData.sort((a, b) => {
          const propA = a.categoryData?.category_propertynumber?.value || '';
          const propB = b.categoryData?.category_propertynumber?.value || '';
          return propA.localeCompare(propB);
        });

        // Update the hotels map for O(1) lookups
        hotelsMapRef.current = new Map(sortedHotels.map(hotel => [hotel.id, hotel]));

        setHotels(sortedHotels);
        setFilteredHotels(sortedHotels);
        setDataReady(true);
        setIsInitialLoad(false);
      } catch (error) {
        console.error('Error fetching hotels:', error);
        setSnackbar({
          open: true,
          message: 'Error loading hotels. Please try again.',
          severity: 'error',
          autoHideDuration: 4000
        });
      }
    };

    fetchHotels();
  }, [userUid, showArchivedHotels]);

  // Handle URL-based hotel selection
  useEffect(() => {
    // #region agent log
    try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'urlSyncEffect',path:location.pathname,dataReady,hCount:hotels.length,selHotel:selectedHotel?.id||null,isNav:isNavigatingRef.current});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
    // #endregion
    // Skip if we're navigating programmatically (to prevent double-updates)
    if (isNavigatingRef.current) return;
    
    // Only run when hotels are loaded
    if (!dataReady || hotels.length === 0) return;
    
    // Check if URL matches /app/hotel/:propertyNumber pattern (only this route owns hotel state)
    const match = location.pathname.match(/^\/app\/hotel\/([^/]+)$/);
    
    // If we're not on a hotel detail URL, clear selection so state matches the route.
    // This prevents "bouncing back" when e.g. user auto-opened a hotel then navigates to
    // /app/documentation — we must clear selectedHotel so no other logic redirects to /app.
    if (!match) {
      if (selectedHotel) {
        setSelectedHotel(null);
      }
      return;
    }
    
    const propertyNumberFromUrl = match[1]?.toLowerCase();
    
    // Find hotel by property number (check multiple locations where it might be stored)
    const hotel = hotels.find(h => {
      const hotelPropertyNumber = (h.id || 
                                 h.propertyNumber || 
                                 h.categoryData?.category_propertynumber?.value)?.toLowerCase();
      return hotelPropertyNumber === propertyNumberFromUrl;
    });
    
    if (hotel) {
      // Only update if the hotel is different from what's currently selected
      const currentPropertyNumber = (selectedHotel?.id || 
                                    selectedHotel?.propertyNumber || 
                                    selectedHotel?.categoryData?.category_propertynumber?.value)?.toLowerCase();
      
      if (currentPropertyNumber !== propertyNumberFromUrl) {
        setSelectedHotel(hotel);
        setIsAutoSelected(false);
      }
    } else if (selectedHotel) {
      // #region agent log
      try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'urlSyncEffect:REDIRECT',path:location.pathname,selHotel:selectedHotel?.id,hCount:hotels.length});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
      // #endregion
      console.warn('[Nav] URL-sync effect redirecting to /app — hotel not in list', { path: location.pathname, hotelId: selectedHotel?.id });
      // Only redirect when we're actually on a hotel URL and the hotel isn't in the list
      setSelectedHotel(null);
      navigate('/app', { replace: true });
    }
  }, [location.pathname, hotels, dataReady, selectedHotel, navigate]);

  // Replace the real-time updates effect
  useEffect(() => {
    if (!user || !selectedHotel || isInitialLoad) return;

    // Clean up previous listener and debounced update
    if (selectedHotelListenerRef.current) {
      selectedHotelListenerRef.current();
      selectedHotelListenerRef.current = null;
    }
    if (debouncedUpdateRef.current) {
      debouncedUpdateRef.current.cancel();
    }

    // Optimized change detection function
    const hasRelevantChanges = (oldHotel, newHotel) => {
      if (!oldHotel || !newHotel) return true;
      
      // Only check fields that affect the UI and compare them directly
      const relevantFields = ['categoryData', 'tags', 'status', 'notes'];
      
      // Compare each field individually for better precision
      for (const field of relevantFields) {
        const oldValue = oldHotel[field];
        const newValue = newHotel[field];
        
        // Skip undefined fields
        if (oldValue === undefined && newValue === undefined) continue;
        
        // If one exists and the other doesn't, there's a change
        if ((!oldValue && newValue) || (oldValue && !newValue)) return true;
        
        // For notes array, compare content only to avoid metadata changes
        if (field === 'notes' && Array.isArray(oldValue) && Array.isArray(newValue)) {
          // Skip if both are empty arrays
          if (oldValue.length === 0 && newValue.length === 0) continue;
          
          // If lengths differ, there's a change
          if (oldValue.length !== newValue.length) return true;
          
          // Compare content only, ignoring timestamps
          const oldContents = oldValue.map(note => note.content).filter(Boolean);
          const newContents = newValue.map(note => note.content).filter(Boolean);
          if (JSON.stringify(oldContents) !== JSON.stringify(newContents)) return true;
          
          continue;
        }
        
        // For categoryData, compare each category field individually
        if (field === 'categoryData' && oldValue && newValue) {
          const allKeys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);
          
          for (const key of allKeys) {
            const oldCat = oldValue[key];
            const newCat = newValue[key];
            
            // If keys exist in only one object, there's a change
            if ((!oldCat && newCat) || (oldCat && !newCat)) return true;
            
            // Compare the 'value' property only
            if (oldCat?.value !== newCat?.value) return true;
          }
          
          continue;
        }
        
        // For other fields, use stringified comparison
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) return true;
      }
      
      // No relevant changes found
      return false;
    };

    // Create debounced update function with optimized batching
    debouncedUpdateRef.current = debounce((updatedHotel) => {
      const currentHotel = hotelsMapRef.current.get(updatedHotel.id);
      
      if (hasRelevantChanges(currentHotel, updatedHotel)) {
        console.log('🔄 Applying hotel update due to relevant changes');
        // Update the hotels map
        hotelsMapRef.current.set(updatedHotel.id, updatedHotel);

        // Use React 18's automatic batching
        const batchUpdate = () => {
          setSelectedHotel(updatedHotel);
          
          setHotels(prevHotels => {
            const hotelIndex = prevHotels.findIndex(h => h.id === updatedHotel.id);
            if (hotelIndex === -1) return prevHotels;
            
            const newHotels = [...prevHotels];
            newHotels[hotelIndex] = updatedHotel;
            return newHotels;
          });

          // Only update filtered hotels if the hotel is actually in the filtered list
          setFilteredHotels(prevHotels => {
            const hotelIndex = prevHotels.findIndex(h => h.id === updatedHotel.id);
            if (hotelIndex === -1) return prevHotels;
            
            const newHotels = [...prevHotels];
            newHotels[hotelIndex] = updatedHotel;
            return newHotels;
          });
        };

        // Execute the batch update
        batchUpdate();
      } else {
        console.log('🔄 Skipping hotel update - no relevant changes detected');
      }
    }, 300); // Increased debounce time to reduce updates

    // Set up listener for selected hotel with error recovery
    const setupListener = () => {
      const hotelRef = doc(db, 'hotels', selectedHotel.id);
      selectedHotelListenerRef.current = onSnapshot(
        hotelRef,
        { includeMetadataChanges: false },  // Only listen for actual data changes, not metadata
        (doc) => {
          if (doc.exists()) {
            const updatedHotel = {
              id: doc.id,
              ...doc.data()
            };
            debouncedUpdateRef.current(updatedHotel);
          }
        },
        (error) => {
          console.error('Error in hotel listener:', error);
          // Attempt to reconnect after a brief delay
          setTimeout(setupListener, 5000);
          setSnackbar({
            open: true,
            message: 'Connection lost. Attempting to reconnect...',
            severity: 'warning',
            autoHideDuration: 4000
          });
        }
      );

      console.log('🔄 Hotel listener setup for:', selectedHotel.id);
    };

    setupListener();

    // Enhanced cleanup function
    return () => {
      if (selectedHotelListenerRef.current) {
        selectedHotelListenerRef.current();
        selectedHotelListenerRef.current = null;
      }
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.cancel();
        debouncedUpdateRef.current = null;
      }
    };
  }, [user, selectedHotel, isInitialLoad]);

  // Add development mode keyboard shortcut for testing updates
  useEffect(() => {
    const handleTestUpdate = (e) => {
      // Check if we're in development mode
      if (process.env.NODE_ENV === 'development') {
        // Ctrl + Alt + U combination
        if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'u') {
          handleUpdateConfirm(); // Use handleUpdateConfirm from useVersionCheck hook
        }
      }
    };

    window.addEventListener('keydown', handleTestUpdate);
    return () => window.removeEventListener('keydown', handleTestUpdate);
  }, [handleUpdateConfirm]); // Add handleUpdateConfirm to dependencies

  // Add the recently viewed functions
  const updateRecentlyViewed = useCallback((hotel) => {
    if (!hotel) return;
    
    // Get current recently viewed from cookies
    const recentlyViewed = Cookies.get('recentlyViewedHotels');
    let recentList = recentlyViewed ? JSON.parse(recentlyViewed) : [];
    
    // Remove the hotel if it already exists in the list
    recentList = recentList.filter(h => h.id !== hotel.id);
    
    // Add the hotel to the beginning of the list
    recentList.unshift({
      id: hotel.id,
      name: hotel.name || hotel.categoryData?.category_name?.value,
      chain: hotel.categoryData?.category_chain?.value
    });
    
    // Keep only the last 3 hotels
    recentList = recentList.slice(0, 3);
    
    // Update cookie and state
    Cookies.set('recentlyViewedHotels', JSON.stringify(recentList), { expires: 30 });
    setRecentlyViewedHotels(recentList);
  }, []);

  // Load recently viewed hotels from cookie on mount
  useEffect(() => {
    const recentlyViewed = Cookies.get('recentlyViewedHotels');
    if (recentlyViewed) {
      setRecentlyViewedHotels(JSON.parse(recentlyViewed));
    }
  }, []);

  // Update recently viewed when a hotel is selected
  useEffect(() => {
    if (selectedHotel) {
      updateRecentlyViewed(selectedHotel);
    }
  }, [selectedHotel, updateRecentlyViewed]);

  // Function to clear recently viewed hotels
  const clearRecentlyViewed = useCallback(() => {
    Cookies.remove('recentlyViewedHotels');
    setRecentlyViewedHotels([]);
  }, []);

  const handleHeroIconClick = () => {
    // Expanded magical color palette with shimmering effects
    const colors = [
      '#FF4D4F', '#FFB3B3', '#FF8C94', // Reds
      '#40A9FF', '#91D5FF', '#69C0FF', // Blues
      '#73D13D', '#B7EB8F', '#95DE64', // Greens
      '#FFC53D', '#FFE58F', '#FFD666', // Golds
      '#9C27B0', '#E6A3F7', '#D3ADF7', // Purples
      '#FF9800', '#FFD591', '#FFC069', // Oranges
      '#F759AB', '#FFA39E', '#FFA940', // Special accents
      '#FFFFFF', '#F0F0F0'             // Sparkles
    ];

    // Create enhanced rain effect with multiple types of particles
    const count = 10; // Increased number of waves
    
    // Function to create varied particle settings with enhanced magic
    function getParticleSettings(type = 'normal') {
      const baseSettings = {
        origin: { y: -0.4 },
        disableForReducedMotion: true,
        colors: colors.sort(() => Math.random() - 0.5).slice(0, 6)
      };

      switch(type) {
        case 'sparkle':
          return {
            ...baseSettings,
            particleCount: 8,
            spread: 60,
            gravity: 0.3,
            scalar: 0.4,
            drift: Math.random() * 2 - 1,
            ticks: 500,
            shapes: ['star', 'circle'],
            colors: ['#FFFFFF', '#F0F0F0', '#FFE58F', '#91D5FF']
          };
        case 'floating':
          return {
            ...baseSettings,
            particleCount: 15,
            spread: 80,
            gravity: 0.25,
            scalar: 1.2,
            drift: Math.random() * 3 - 1.5,
            ticks: 400,
            shapes: ['circle', 'square'],
          };
        case 'tiny':
          return {
            ...baseSettings,
            particleCount: 25,
            spread: 45,
            gravity: 0.6,
            scalar: 0.3,
            drift: Math.random() * 0.5 - 0.25,
            ticks: 300,
            shapes: ['circle']
          };
        default:
          return {
            ...baseSettings,
            particleCount: Math.floor(Math.random() * 15) + 15,
            spread: Math.random() * 40 + 40,
            gravity: 0.7 + Math.random() * 0.3,
            ticks: 300 + Math.floor(Math.random() * 200),
            shapes: ['circle', 'square', 'star'],
            scalar: 0.8 + Math.random() * 1.2,
            drift: Math.random() * 1 - 0.5
          };
      }
    }

    // Launch confetti from random positions with enhanced patterns
    function launchFrom(x) {
      // Main burst
      confetti({
        ...getParticleSettings('normal'),
        origin: { x, y: -0.5 },
        angle: -90 + (Math.random() * 30 - 15),
      });

      // Sparkles
      confetti({
        ...getParticleSettings('sparkle'),
        origin: { x, y: -0.2 }
      });

      // Floating pieces
      confetti({
        ...getParticleSettings('floating'),
        origin: { x, y: -0.4 }
      });

      // Tiny particles
      confetti({
        ...getParticleSettings('tiny'),
        origin: { x, y: -0.1 }
      });
    }

    // Create a more dynamic launch pattern
    function createMagicalBurst() {
      // Initial burst
      for (let i = 0; i < 6; i++) {
        setTimeout(() => {
          launchFrom(0.2 + Math.random() * 0.6); // Concentrate more in the center
        }, i * 50); // Quick initial burst
      }

      // Continuous magical rain
      for (let i = 1; i < count; i++) {
        setTimeout(() => {
          for (let j = 0; j < 3; j++) {
            launchFrom(Math.random());
          }
        }, 300 + (i * 100)); // Start after initial burst
      }

      // Extra sparkles
      for (let i = 0; i < 8; i++) {
        setTimeout(() => {
          confetti({
            ...getParticleSettings('sparkle'),
            origin: { x: 0.2 + Math.random() * 0.6, y: -0.2 },
            gravity: 0.4,
            scalar: 0.3
          });
        }, 500 + (i * 200)); // Delayed sparkle effect
      }
    }

    // Start the magical show
    createMagicalBurst();
  };

  // Get the title and options based on current state
  const getTitleConfig = () => {
    // Handle login page
    if (isLoginPage) {
      return { title: 'Sign In' };
    }

    // Handle selected hotel
    if (selectedHotel) {
      const hotelName = selectedHotel.name || selectedHotel.categoryData?.category_name?.value;
      const chain = selectedHotel.categoryData?.category_chain?.value;
      return {
        title: hotelName,
        prefix: chain || undefined
      };
    }

    // Handle search
    if (searchQuery) {
      return {
        title: searchQuery,
        prefix: 'Search'
      };
    }

    // Handle different sections
    if (location.pathname.includes('/handbooks')) {
      return { title: 'Handbook Library' };
    }
    if (location.pathname.includes('/category-management')) {
      return { title: 'Category Management' };
    }
    if (location.pathname.includes('/user-management')) {
      return { title: 'User Management' };
    }
    if (location.pathname.includes('/theme')) {
      return { title: 'Theme Settings' };
    }
    if (location.pathname.includes('/theme-preview')) {
      return { title: 'Theme Preview' };
    }

    return { title: null };
  };

  // Calculate notifications (if any)
  const notificationCount = updateAvailable ? 1 : 0;

  // Use the hook with all options
  useDocumentTitle(getTitleConfig().title, {
    ...getTitleConfig(),
    notifications: notificationCount,
    isError: false // You can set this based on any error state in your app
  });

  // Add this effect to check app version
  useEffect(() => {
    const checkAppVersion = async () => {
      try {
        // Skip if on login page
        if (location.pathname === '/login') {
          return;
        }
        
        // Only check version if user is authenticated
        const auth = getAuth();
        if (!auth.currentUser) {
          return;
        }
        
        // Get current version from Firestore
        const versionDoc = await getDoc(doc(db, 'app_settings', 'version'));
        if (versionDoc.exists()) {
          const serverVersion = versionDoc.data().version;
          const currentVersion = localStorage.getItem('appVersion');
          
          // If versions don't match, update localStorage and force refresh
          if (serverVersion !== currentVersion) {
            console.log(`App update detected: ${currentVersion} → ${serverVersion}`);
            localStorage.setItem('appVersion', serverVersion);
            window.location.reload(true);
          }
        }
      } catch (error) {
        console.error('Error checking app version:', error);
      }
    };
    
    // Check immediately and then every 5 minutes
    checkAppVersion();
    const versionCheckInterval = setInterval(checkAppVersion, 5 * 60 * 1000);
    
    return () => clearInterval(versionCheckInterval);
  }, [location.pathname]);

  if (!themeLoaded) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', 
        bgcolor: 'background.default',
        animation: 'fadeIn 0.3s ease-out',
        '@keyframes fadeIn': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}>
        {/* Header - Only show if not on login page */}
        {!isLoginPage && (
          <Header 
            user={user}
            darkMode={darkMode}
            onDarkModeToggle={() => {
              const newMode = !darkMode;
              setDarkMode(newMode);
              Cookies.set('darkMode', newMode.toString(), { expires: 365 });
            }}
            setSnackbar={setSnackbar}
            onStatsOpen={() => setStatsDialogOpen(true)}
            onLogout={handleLogout}
            useColoredCategories={useColoredCategories}
            onToggleColoredCategories={() => {
              setUseColoredCategories(!useColoredCategories);
              localStorage.setItem('useColoredCategories', JSON.stringify(!useColoredCategories));
            }}
            onToggleCompactView={handleCompactViewToggle}
            filters={filters}
            sidebarCollapsed={sidebarCollapsed}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            searchInputRef={searchInputRef}
            hotels={hotels}
            onHotelSelect={handleHotelSelect}
            onHotelDeselect={() => {
              setSelectedHotel(null);
              isNavigatingRef.current = true;
              navigate('/app', { replace: true });
              setTimeout(() => { isNavigatingRef.current = false; }, 100);
            }}
            onBeforeNavClick={() => {
              isNavigatingRef.current = true;
              setSelectedHotel(null);
              setTimeout(() => { isNavigatingRef.current = false; }, 400);
            }}
          />
        )}

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden'
        }}>
          <Routes>
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="/login" element={<LoginPage />} />
            
            {/* Specific routes MUST come before /app/* catch-all to ensure proper matching */}
            {/* Admin-only routes */}
            {user?.role === 'admin' && (
              <>
                <Route path="/app/category-management" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <CategoryManager />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/app/user-management" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/hotels" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <HotelManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/app/activity-logs" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <ActivityLogs />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/app/chain-management" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <ChainManager />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/app/force-refresh" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <Layout>
                      <ForceRefresh />
                    </Layout>
                  </ProtectedRoute>
                } />
              </>
            )}

            {/* Dashboard Route - Accessible to all authenticated users */}
            <Route path="/app/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <HotelDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/app/theme" element={
              <ProtectedRoute>
                <Layout>
                  <ThemeSettings />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/app/theme-preview" element={
              <ProtectedRoute>
                <ThemePreview />
              </ProtectedRoute>
            } />
            
            {/* Routes accessible to admin and SOS users only */}
            <Route path="/app/handbooks" element={
              <ProtectedRoute requiredRoles={['admin', 'SOS']}>
                <Layout>
                  <EnhancedHandbookLibrary />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Google Contacts route - admin and SOS only */}
            <Route path="/app/contacts" element={
              <ProtectedRoute requiredRoles={['admin', 'SOS']}>
                <Layout>
                  <GoogleContactsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Scripts route - admin and SOS only */}
            <Route path="/app/scripts" element={
              <ProtectedRoute requiredRoles={['admin', 'SOS']}>
                <Layout>
                  <ScriptsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Feature Requests route - accessible to all users */}
            <Route path="/app/feature-requests" element={
              <ProtectedRoute>
                <Layout>
                  <FeatureRequestPage user={user} />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Documentation route - restricted to admin and SOS users */}
            <Route path="/app/documentation" element={
              <ProtectedRoute requiredRoles={['admin', 'SOS']}>
                <Layout>
                  <Documentation />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Weekly Statistics route - admin and SOS only */}
            <Route path="/app/weekly-statistics" element={
              <ProtectedRoute requiredRoles={['admin', 'SOS']}>
                <Layout>
                  <WeeklyStatistics />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Catch-all /app/* route - must come AFTER all specific routes */}
            <Route path="/app/*" element={
              <ProtectedRoute>
                <AppShellOrNotFound>
                <Box sx={{ 
                  height: '100vh', // Full viewport height
                  overflow: 'hidden',
                  display: 'flex',
                  position: 'relative'
                }}>
                  {/* Fixed Hotel List Sidebar - Connected to Header */}
                  <Box sx={{
                    width: { xs: '100%', md: '380px' }, // Fixed width - no width animation
                    height: '100vh', // Full height from top
                    position: { xs: 'relative', md: 'fixed' },
                    left: 0,
                    top: 0, // Start from very top
                    zIndex: 1100, // Below header but above content
                    bgcolor: 'background.default', // Switched to default (now lighter)
                    display: { xs: selectedHotel ? 'none' : 'flex', md: 'flex' },
                    flexDirection: 'column',
                    pt: { xs: 0, md: '85px' }, // Account for header height
                    overflow: 'hidden', // Hide content when collapsed
                    overflowY: 'auto', // Allow vertical scrolling
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Only transform transition
                    transform: { xs: 'none', md: sidebarCollapsed ? 'translateX(-100%)' : 'translateX(0)' }, // Clean slide animation
                  }}>
                    <HotelList
                      searchQuery={searchQuery}
                      onSearchChange={handleSearchChange}
                      filters={filters}
                      filteredHotels={filteredHotels}
                      selectedHotel={selectedHotel}
                      onHotelSelect={handleHotelSelect}
                      filterLoading={filterLoading}
                      dataReady={dataReady}
                      setFilters={setFilters}
                      setFilteredHotels={setFilteredHotels}
                      hotels={hotels}
                      createFilterOptions={createFilterOptions}
                      searchInputRef={searchInputRef}
                      filterDialogOpen={filterDialogOpen}
                      setFilterDialogOpen={setFilterDialogOpen}
                      onFilterChange={handleFilterChange}
                      sidebarCollapsed={sidebarCollapsed}
                      onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                      showArchivedHotels={showArchivedHotels}
                      onToggleArchivedHotels={() => setShowArchivedHotels(!showArchivedHotels)}
                    />
                  </Box>

                  {/* Main Content Area - Offset for sidebar */}
                  <Box sx={{ 
                    flex: 1,
                    marginLeft: { xs: 0, md: sidebarCollapsed ? '0px' : '380px' }, // Dynamic offset based on sidebar state
                    width: { xs: '100%', md: sidebarCollapsed ? '100%' : 'calc(100% - 380px)' }, // Dynamic width calculation
                    height: '100vh',
                    overflowY: 'auto',
                    bgcolor: 'background.default', // Switched to default (now lighter)
                    display: { xs: selectedHotel ? 'block' : 'none', md: 'block' },
                    pt: { xs: '70px', sm: '85px' }, // Account for header height
                    transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transitions for both margin and width
                    transform: 'translateZ(0)', // Force hardware acceleration for smoother animations
                    ...ThemeStyles.scrollbar(theme)
                  }}>
                    {!selectedHotel ? (
                      <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for inner container
                        transform: 'translateZ(0)', // Force hardware acceleration
                      }}>
                        {sidebarCollapsed ? (
                          <NoHotelSelected 
                            hotels={hotels}
                            filteredHotels={filteredHotels}
                            recentlyViewedHotels={recentlyViewedHotels}
                            handleHeroIconClick={handleHeroIconClick}
                            randomIcon={randomIcon}
                            user={user}
                            clearRecentlyViewed={clearRecentlyViewed}
                            setSelectedHotel={setSelectedHotel}
                            setFilters={setFilters}
                            setFilteredHotels={setFilteredHotels}
                            sidebarCollapsed={sidebarCollapsed}
                            searchQuery={searchQuery}
                            onSearchChange={handleSearchChange}
                            onHotelSelect={setSelectedHotel}
                          />
                        ) : (
                          <NoHotelSelectedSimple />
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ 
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth transition for inner container
                        transform: 'translateZ(0)', // Force hardware acceleration
                      }}>
                        <HotelDetails
                          selectedHotel={selectedHotel}
                          setSelectedHotel={setSelectedHotel}
                          isEditing={isEditing}
                          editedHotel={editedHotel}
                          handleEditClick={handleEditClick}
                          handleSaveEdit={handleSaveEdit}
                          handleCancelEdit={handleCancelEdit}
                          handleEditFieldChange={handleEditFieldChange}
                          getCategoryValue={getCategoryValue}
                          categories={categories}
                          useColoredCategories={useColoredCategories}
                          handleTagsChange={handleTagsChange}
                          handleCopyClick={handleCopyClick}
                          isSaving={isSaving}
                          saveStatus={hotelSaveStatus}
                          saveStatusMessage={hotelSaveStatusMessage}
                          saveStatusOpen={hotelSaveStatusOpen}
                          onSaveStatusExited={() => {
                            setHotelSaveStatus(null);
                            setHotelSaveStatusMessage('');
                          }}
                          compactView={user?.themePreferences?.compactView || false}
                          sidebarCollapsed={sidebarCollapsed}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Edge Tab Toggle Button - Only visible when sidebar is collapsed */}
                  {sidebarCollapsed && (
                    <Box
                      onClick={() => setSidebarCollapsed(false)}
                      sx={{
                        position: 'fixed',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '24px',
                        height: '120px',
                        bgcolor: 'background.paper',
                        borderRadius: '0 20px 20px 0',
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 1200,
                        borderTop: theme => `1px solid ${theme.palette.divider}`,
                        borderRight: theme => `1px solid ${theme.palette.divider}`,
                        borderBottom: theme => `1px solid ${theme.palette.divider}`,
                        borderLeft: 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.12)}`,
                        '&:hover': {
                          width: '32px',
                          bgcolor: 'primary.main',
                          boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.12)}`,
                          transform: 'translateY(-50%) scale(1.05)',
                          '& .MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }
                      }}
                    >
                      <ChevronRightIcon sx={{ 
                        color: 'white', 
                        fontSize: '1.2rem',
                        transition: 'color 0.3s ease'
                      }} />
                    </Box>
                  )}

                  {/* Collapse Button - Only visible when sidebar is expanded */}
                  {!sidebarCollapsed && (
                    <Box
                      onClick={() => setSidebarCollapsed(true)}
                      sx={{
                        position: 'fixed',
                        top: '50%',
                        right: { xs: 'auto', md: 'calc(100% - 380px - 25px)' }, // 5px padding from sidebar edge, entire button shifted left
                        transform: 'translateY(-50%)',
                        width: '24px',
                        height: '120px',
                        bgcolor: 'background.paper',
                        borderRadius: '20px',
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 1200, // Same as edge tab
                        '&:hover': {
                          bgcolor: 'primary.main',
                          '& .MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }
                      }}
                    >
                      <ChevronLeftIcon sx={{ 
                        color: 'text.secondary', 
                        fontSize: '1.2rem'
                      }} />
                    </Box>
                  )}

                </Box>
                </AppShellOrNotFound>
              </ProtectedRoute>
            } />
            {/* Catch-all: 404 for unknown routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>
      </Box>
      
      {/* Add Statistics Dialog */}
      <StatisticsDialog
        open={statsDialogOpen} 
        onClose={() => setStatsDialogOpen(false)}
        hotels={hotels}
        categories={categories}
        chainDistribution={chainDistribution}
        systemUsage={systemUsage}
        darkMode={darkMode}
      />
      
      {/* Add UpdateNotification component */}
      <UpdateNotification 
        open={updateAvailable} 
        onClose={handleUpdateLater}
        onUpdate={handleUpdateConfirm}
      />
      
      {/* Add Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
        sx={{
          ...ThemeStyles.snackbar(theme),
          '& .MuiPaper-root, & .MuiAlert-root': {
            '&::after': { display: 'none' },
          },
        }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            position: 'relative',
            zIndex: 2,
            '& .MuiAlert-message': {
              fontFamily: 'StrawberrySansText',
              fontSize: '0.875rem',
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Add Jakob Mode Dialog */}
      <Dialog
        open={jakobDialog.open}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            animation: 'fadeInDramatic 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes fadeInDramatic': {
              '0%': { opacity: 0 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 }
            }
          }
        }}
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            m: 0,
            borderRadius: 4,
            overflow: 'hidden',
            animation: 'appearDramatic 1.5s cubic-bezier(0.19, 1, 0.22, 1)',
            '@keyframes appearDramatic': {
              '0%': { 
                transform: 'scale(0) rotate(-10deg)', 
                opacity: 0,
                filter: 'blur(20px)'
              },
              '60%': { 
                transform: 'scale(1.2) rotate(5deg)', 
                opacity: 0.8,
                filter: 'blur(0px)'
              },
              '80%': { 
                transform: 'scale(0.95) rotate(-2deg)',
                opacity: 0.9
              },
              '100%': { 
                transform: 'scale(1) rotate(0deg)',
                opacity: 1
              }
            }
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            p: 8,
            minWidth: '600px',
            textAlign: 'center',
            background: theme => `radial-gradient(circle at center,
              ${alpha(theme.palette.pink.main, 0.98)} 0%,
              ${alpha(theme.palette.pink.dark, 0.98)} 100%)`,
            border: '4px solid',
            borderColor: 'pink.light',
            borderRadius: '30px',
            boxShadow: theme => `
              0 0 80px ${alpha(theme.palette.pink.main, 0.6)},
              0 0 160px ${alpha(theme.palette.pink.main, 0.3)},
              0 0 240px ${alpha(theme.palette.pink.main, 0.15)},
              inset 0 0 80px ${alpha(theme.palette.pink.main, 0.4)}
            `,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              right: '-3px',
              bottom: '-3px',
              background: theme => `linear-gradient(45deg, 
                ${alpha(theme.palette.pink.light, 0.7)},
                ${alpha(theme.palette.pink.main, 0.7)},
                ${alpha(theme.palette.pink.dark, 0.7)})`,
              borderRadius: 'inherit',
              zIndex: -1,
              animation: 'borderGlowEnhanced 3s infinite',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.3), transparent 70%)',
              borderRadius: 'inherit',
              animation: 'pulseGlow 2s infinite',
            },
            '@keyframes borderGlowEnhanced': {
              '0%, 100%': {
                opacity: 1,
                transform: 'scale(1) rotate(0deg)'
              },
              '50%': {
                opacity: 0.8,
                transform: 'scale(1.03) rotate(1deg)'
              }
            },
            '@keyframes pulseGlow': {
              '0%, 100%': {
                opacity: 0.3,
                transform: 'scale(1)'
              },
              '50%': {
                opacity: 0.6,
                transform: 'scale(1.1)'
              }
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              '&::before, &::after': {
                content: '"✨"',
                position: 'absolute',
                fontSize: '32px',
                animation: 'floatingStar 3s infinite',
              },
              '&::before': {
                top: '-40px',
                left: '-30px',
                animationDelay: '0s',
              },
              '&::after': {
                bottom: '-40px',
                right: '-30px',
                animationDelay: '1.5s',
              },
              '@keyframes floatingStar': {
                '0%, 100%': {
                  opacity: 1,
                  transform: 'scale(1) rotate(0deg) translate(0, 0)',
                },
                '25%': {
                  transform: 'scale(1.2) rotate(90deg) translate(10px, -10px)',
                },
                '50%': {
                  opacity: 0.7,
                  transform: 'scale(0.8) rotate(180deg) translate(0, 0)',
                },
                '75%': {
                  transform: 'scale(1.1) rotate(270deg) translate(-10px, 10px)',
                }
              }
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'StrawberrySansDisplay',
                color: 'white',
                textShadow: theme => `
                  0 0 30px ${alpha(theme.palette.pink.main, 0.9)},
                  0 0 60px ${alpha(theme.palette.pink.main, 0.6)},
                  0 0 90px ${alpha(theme.palette.pink.main, 0.3)},
                  0 2px 4px rgba(0,0,0,0.3)
                `,
                animation: 'textReveal 1.2s cubic-bezier(0.19, 1, 0.22, 1)',
                letterSpacing: '0.08em',
                fontWeight: 700,
                fontSize: '2.8rem',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-20px -40px',
                  background: 'radial-gradient(circle at center, rgba(255,255,255,0.2), transparent 70%)',
                  animation: 'pulseBackground 2s infinite',
                },
                '@keyframes textReveal': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.5) translateY(50px)',
                    filter: 'blur(20px)'
                  },
                  '60%': {
                    opacity: 0.8,
                    transform: 'scale(1.1) translateY(-10px)',
                    filter: 'blur(0px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1) translateY(0)',
                  }
                },
                '@keyframes pulseBackground': {
                  '0%, 100%': {
                    opacity: 0.2,
                    transform: 'scale(1)'
                  },
                  '50%': {
                    opacity: 0.4,
                    transform: 'scale(1.1)'
                  }
                }
              }}
            >
              {jakobDialog.message}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <CategoriesProvider>
        <ChainProvider>
          <AppContent />
        </ChainProvider>
      </CategoriesProvider>
    </AuthProvider>
  );
}

export default App;
